import { Product } from '@sparknz/tracking';

import { getPromotionByType, PromotionType } from 'shared/utils';
import { Channel } from '../../../../shared/hooks';
import { UPFRONT_PAYMENT_ID } from '../../constants/ifp.constants';
import { LinkedProductsTrackingArgs } from '../../typings';
import {
  constructPriceAttributes,
  constructPromotionAttributes,
} from '../productAttributes.utils';

export function constructAnalyticsProduct({
  device,
  plan,
  paymentOption,
  isBusiness,
  isChangePlan,
  isSpotifyOptIn,
}: LinkedProductsTrackingArgs): Product {
  const isDeferredPayment = Boolean(paymentOption?.id !== UPFRONT_PAYMENT_ID);

  const ifpOneOffPaymentAmount =
    paymentOption?.minimumDownPaymentAmount || '0.00';

  const segment = isBusiness ? 'BUSINESS' : 'CONSUMER';
  const baseStore: Channel = isBusiness ? 'businessshop' : 'personalshop';
  const deviceImage = device.images?.[0];
  const ifpRecurringPrice =
    paymentOption?.effectivePrice ?? paymentOption?.basePrice;
  const promoDeals = getPromotionByType(
    device.promotions,
    PromotionType.DEAL,
  )?.name;

  const isGiftProduct = plan?.dataLayerCategory === 'GiftProduct';

  return {
    attributes: {
      baseStore,
      categoryIdentifier: device.categoryIds.join(','),
      contractTerm: paymentOption?.name || 'Open term',
      deferredPaymentOption: paymentOption?.name,
      displayName: device.groupName,
      itemBasePrice: String(device.basePrice),
      itemOneOffCost: isDeferredPayment
        ? String(ifpOneOffPaymentAmount)
        : String(device.basePrice),
      itemRecurringCost: isDeferredPayment ? String(ifpRecurringPrice) : '0.00',
      promoDeals,
      ...constructPromotionAttributes(device.promotions),
      isDeferredPayment,
      isGSTIncluded: !isBusiness,
      isGiftWithPurchase: device?.dataLayerCategory === 'GiftProduct',
      isHero: Boolean(device.isDefault),
      isInStock: [
        device.stock?.storeStatus,
        device.stock?.warehouseStatus,
      ].includes('IN_STOCK'),
      stockInfo: {
        isInStockWarehouse: device.stock?.warehouseStatus === 'IN_STOCK',
        isInStockStore: device.stock?.storeStatus === 'IN_STOCK',
      },
      isPreOrder: device.preOrderable,
      paymentType: device.balanceManagement,
      segment,
      servicePlanName: plan?.name,
      servicePlanType: plan?.productCharacteristics?.balanceManagement,
      productType: device?.productType,
      productLabel: device?.productLabel,
      ...(isChangePlan !== undefined && { isChangePlan }),
      ...(isSpotifyOptIn !== undefined && { isSpotifyOptIn }),
    },
    category: {
      primaryCategory: device.dataLayerCategory,
    },
    productInfo: {
      color: device.color,
      manufacturer: device.brand,
      productID: device?.id,
      productImage: deviceImage?.standardUrl || deviceImage?.defaultUrl,
      productName: device.name,
      size: device.storage,
      sku: device.externalId,
    },
    quantity: '1',
    // Only add a linkedProduct if a plan is passed
    linkedProduct: plan && [
      {
        productInfo: {
          productID: plan.id,
          productName: plan.name,
          manufacturer: 'Spark',
          sku: plan.id,
          productImage: null,
        },
        category: {
          primaryCategory: plan.dataLayerCategory,
          subCategory1: plan.category[1]?.id,
        },
        attributes: {
          baseStore,
          categoryIdentifier: plan.category.map(({ id }) => id).join(','),
          contractTerm: plan.contractTerm?.name,
          ...constructPriceAttributes(plan.price),
          ...constructPromotionAttributes(plan.promotions),
          deferredPaymentOption: paymentOption?.name,
          deviceName: device.name,
          displayName: plan.name,
          isDeferredPayment,
          isGSTIncluded: !isBusiness,
          isGiftWithPurchase: isGiftProduct,
          paymentType: plan.productCharacteristics.balanceManagement,
          segment,
          ...(isChangePlan !== undefined && { isChangePlan }),
          ...(isSpotifyOptIn !== undefined && { isSpotifyOptIn }),
        },
      },
    ],
  };
}
